import React, { useMemo } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

interface ErrorDistributionChartProps {
  data: number[];
  loading?: boolean;
}

const ErrorDistributionChart: React.FC<ErrorDistributionChartProps> = ({ data, loading }) => {
  if (loading) {
    return (
      <div className="h-48 flex items-center justify-center">
        <div className="animate-pulse bg-gradient-to-r from-slate-200 via-slate-300 to-slate-200 w-full h-full rounded-xl"></div>
      </div>
    );
  }

  const chartData = useMemo(() => {
    const bins = 10;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const binSize = (max - min) / bins;
    
    const histogram = Array.from({ length: bins }, (_, i) => {
      const binStart = min + i * binSize;
      const binEnd = binStart + binSize;
      const count = data.filter(val => val >= binStart && val < binEnd).length;
      return {
        range: `${binStart.toFixed(1)}-${binEnd.toFixed(1)}`,
        count,
        percentage: Math.round((count / data.length) * 100)
      };
    });
    
    return histogram;
  }, [data]);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-slate-800/95 border border-slate-600/50 rounded-xl p-4 shadow-xl backdrop-blur-sm">
          <p className="text-slate-200 font-semibold mb-2">Error Range: {label}</p>
          <p className="text-violet-400 font-bold">Count: {data.count}</p>
          <p className="text-green-400">Percentage: {data.percentage}%</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="w-full h-48 bg-gradient-to-br from-slate-50 to-white rounded-xl border border-slate-200 p-4">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData}>
          <CartesianGrid strokeDasharray="2 2" stroke="#e2e8f0" opacity={0.3} />
          <XAxis 
            dataKey="range" 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 10, fill: '#64748b' }}
          />
          <YAxis 
            axisLine={false}
            tickLine={false}
            tick={{ fontSize: 12, fill: '#64748b' }}
          />
          <Tooltip content={<CustomTooltip />} />
          <Bar 
            dataKey="count" 
            fill="#ef4444" 
            radius={[2, 2, 0, 0]}
            animationDuration={300}
          />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ErrorDistributionChart;